// Formatting and validation helpers for the onboarding analytics dashboard
// (CWA-159, spec 009).
//
// Everything here is pure and unit-tested, because these are the four places
// where the dashboard can produce a plausible-looking WRONG answer rather than
// an obvious failure: the funnel monotonicity check, the zero-vs-no-data rule,
// nullable rendering, and month labelling.

import { FunnelStage } from "../types/onboardingAnalytics";

// The repo's existing convention for "no value" in a table cell or a field.
export const EMPTY_CELL = "—";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/**
 * "2026-07" → "Jul '26". Returns the input unchanged when it is not a month key,
 * so an unexpected value degrades to showing what arrived rather than "NaN".
 */
export const monthLabel = (month: string): string => {
  const match = /^(\d{4})-(\d{2})$/.exec(month ?? "");
  if (!match) return month ?? "";
  const monthIndex = Number(match[2]) - 1;
  if (monthIndex < 0 || monthIndex > 11) return month;
  return `${MONTHS[monthIndex]} '${match[1].slice(-2)}`;
};

/**
 * Whether a figure the API returned as a number is a MEASUREMENT or merely the
 * absence of data.
 *
 * The API cannot tell these apart: an empty database and a genuine zero both
 * come back as 0.0 for the conversion rate, the average duration and every
 * segmentation average. Presenting "0 days" for "nothing has ever gone live"
 * states a fact that was never measured, so the caller must decide from the
 * count that stands behind the figure (research R10).
 */
export const hasMeasurement = (count: number | null | undefined): boolean =>
  typeof count === "number" && count > 0;

/**
 * A number of days for display. Returns null when there is nothing to show, so
 * the caller renders its own "no data yet" rather than a zero.
 */
export const formatDays = (
  days: number | null | undefined,
  count?: number | null
): string | null => {
  if (days == null || !Number.isFinite(days)) return null;
  if (count !== undefined && !hasMeasurement(count)) return null;
  const rounded = Math.round(days * 10) / 10;
  return `${rounded} ${rounded === 1 ? "day" : "days"}`;
};

/**
 * A percentage for display. Null in, nothing out — never "0%".
 *
 * The API returns null for a drop-off percentage whose previous stage was empty;
 * rendering that as 0% would claim nobody was lost, which is a different and
 * false statement (FR-020).
 */
export const formatPercent = (
  pct: number | null | undefined
): string | null => {
  if (pct == null || !Number.isFinite(pct)) return null;
  return `${Math.round(pct * 10) / 10}%`;
};

/** A count for display, thousands-separated. */
export const formatCount = (n: number | null | undefined): string =>
  typeof n === "number" && Number.isFinite(n) ? n.toLocaleString("en-GB") : EMPTY_CELL;

/**
 * Anything nullable destined for a cell. Keeps `null`, `undefined` and the empty
 * string out of the DOM (FR-016).
 */
export const orEmpty = (value: string | null | undefined): string =>
  value != null && value !== "" ? value : EMPTY_CELL;

/**
 * Turn an unlabelled segment identifier into something readable. Mirrors
 * humanizeEnum in constants/contractSigning, for values that have no label map
 * of their own (vendors, and lead sources the backend invented).
 */
export const humaniseSegment = (value: string | null | undefined): string => {
  if (!value) return EMPTY_CELL;
  return value
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/^./, (c) => c.toUpperCase());
};

/**
 * The two stages whose counts break the funnel's cumulative invariant, or null
 * when the funnel is sound.
 *
 * Funnel counts are CUMULATIVE — each stage counts prospects at or past it — so
 * in pipeline order they can only decrease. A count that rises means the server
 * ordered its stages wrongly, which makes the drop-off between them negative and
 * the one before it inflated. A chart drawn from that looks entirely plausible
 * and is wrong, which is worse than drawing nothing (research R3, FR-019).
 *
 * This stays as a permanent invariant check, not a workaround: stage order comes
 * from the server, and a future reordering would reintroduce the same silent
 * failure.
 */
export const firstFunnelViolation = (
  stages: FunnelStage[]
): { previous: FunnelStage; current: FunnelStage } | null => {
  for (let i = 1; i < (stages?.length ?? 0); i += 1) {
    const previous = stages[i - 1];
    const current = stages[i];
    if (current.count > previous.count) return { previous, current };
  }
  return null;
};

export const isFunnelMonotonic = (stages: FunnelStage[]): boolean =>
  firstFunnelViolation(stages) === null;

/**
 * A drop-off the API reported as negative is the same fault seen from the other
 * side. Checked separately because a stage can carry one without breaking the
 * count ordering if the server ever computes them independently.
 */
export const hasNegativeDropOff = (stages: FunnelStage[]): boolean =>
  (stages ?? []).some((s) => s.dropOff != null && s.dropOff < 0);
